import { useEffect, useState } from 'react'
import { RefreshCw, MoreVertical, Edit3, Archive, Trash2, Clock } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

// ── Sync Progress Bar ────────────────────────────────────────

export function SyncProgressIndicator({ syncingCount }: { syncingCount: number }) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (syncingCount > 0) setVisible(true)
    else {
      const t = setTimeout(() => setVisible(false), 1000)
      return () => clearTimeout(t)
    }
  }, [syncingCount])

  if (!visible) return null

  return (
    <div className="relative h-1 w-full bg-paper-warm overflow-hidden rounded-full">
      <div className={cn(
        'absolute inset-y-0 left-0 bg-brick rounded-full transition-all',
        syncingCount > 0 ? 'w-2/3 animate-pulse' : 'w-full',
      )} />
    </div>
  )
}

// ── Market Row Action Menu ───────────────────────────────────

interface MarketActionMenuProps {
  onEdit: () => void
  onSync: () => void
  onArchive: () => void
  onDelete: () => void
  isSyncing?: boolean
  lastSync?: string | null
}

export function MarketActionMenu({ onEdit, onSync, onArchive, onDelete, isSyncing, lastSync }: MarketActionMenuProps) {
  const [open, setOpen] = useState(false)

  const actions = [
    { label: 'Modifier', icon: Edit3, onClick: onEdit, variant: 'default' as const },
    { label: isSyncing ? 'Sync en cours…' : 'Relancer sync', icon: RefreshCw, onClick: onSync, variant: 'default' as const },
    { label: 'Archiver', icon: Archive, onClick: onArchive, variant: 'default' as const },
    { label: 'Supprimer', icon: Trash2, onClick: onDelete, variant: 'danger' as const },
  ]

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
        className="p-1.5 rounded-lg text-ink-faint hover:text-ink hover:bg-paper-warm transition-colors"
      >
        <MoreVertical size={16} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-40 w-48 bg-white border border-ink-border rounded-card shadow-elevated py-1 animate-fade-in">
            {lastSync && (
              <div className="px-3 py-2 border-b border-ink-border flex items-center gap-2 text-[10px] text-ink-faint">
                <Clock size={10} />
                Sync: {new Date(lastSync).toLocaleDateString('fr-FR')}
              </div>
            )}
            {actions.map((a) => (
              <button
                key={a.label}
                onClick={(e) => { e.stopPropagation(); a.onClick(); setOpen(false) }}
                disabled={a.label.includes('en cours')}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium transition-colors',
                  a.variant === 'danger' ? 'text-red-600 hover:bg-red-50' : 'text-ink hover:bg-paper',
                  a.label.includes('en cours') && 'opacity-50 cursor-not-allowed',
                )}
              >
                <a.icon size={14} className={isSyncing && a.label.includes('Sync') ? 'animate-spin' : ''} />
                {a.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ── Delete Confirmation Modal ────────────────────────────────

export function DeleteMarketModal({ open, marketName, onConfirm, onClose }: {
  open: boolean; marketName: string; onConfirm: () => void; onClose: () => void
}) {
  const [confirm, setConfirm] = useState('')
  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-2xl border border-ink-border shadow-elevated w-full max-w-sm p-6 animate-slide-up">
        <h3 className="text-lg font-semibold text-ink mb-2">Supprimer ce marché ?</h3>
        <p className="text-sm text-ink-muted mb-4">
          Cette action est irréversible. Toutes les données, keywords, métriques et insights seront supprimés.
        </p>
        <p className="text-xs text-ink mb-2">Tapez <strong>{marketName}</strong> pour confirmer :</p>
        <input
          type="text" value={confirm} onChange={(e) => setConfirm(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
          placeholder={marketName}
        />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-ink hover:bg-paper-warm rounded-lg">Annuler</button>
          <button onClick={onConfirm} disabled={confirm !== marketName}
            className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed">
            Supprimer définitivement
          </button>
        </div>
      </div>
    </div>
  )
}
